import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMdsSubmissionItems,
  MdsWorksheetAggregate,
} from 'src/services/documentService/mdsWorksheet';

/**
 * BRK-1861: assembles the per-item audit history for the worksheet from the
 * append-only submission data. Each submission (mds_submission_item rows)
 * records only the items it touched, so iterating every version and grouping
 * by (itemCode, mdsColumn) yields each field's complete change timeline.
 *
 * Loaded lazily (the drawer is opened per item, not on every page view) and
 * once per version set — the cache is dropped when the anchor changes or a new
 * submission appears (versions grows), so a just-submitted change shows up.
 */

export type MdsAuditEntry = {
  versionNumber: number;
  submittedByRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  submittedByName: string | null;
  submittedAt: string | null;
  itemValue: string | null;
  sourceType: string;
  sourceDocumentType: string | null;
  sourceDateOfService: string | null;
};

const key = (itemCode: string, mdsColumn: string) => `${itemCode}|${mdsColumn}`;

export function useMdsWorksheetAudit(aggregate: MdsWorksheetAggregate | null) {
  const assessmentUuid = aggregate?.assessment?.assessmentUuid;
  const versionCount = aggregate?.versions?.length ?? 0;

  const [entries, setEntries] = useState<Record<string, MdsAuditEntry[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // monotonic request id: a load started for one anchor must never write its
  // result after the anchor changed (item codes are shared across a patient's
  // assessments, so a stale write would silently show the wrong history)
  const requestSeq = useRef(0);

  // drop the cache when the worksheet changes or a new submission lands, and
  // invalidate any in-flight load so it can't resolve into the new anchor
  useEffect(() => {
    requestSeq.current += 1;
    setEntries(null);
    setError(null);
    setLoading(false);
  }, [assessmentUuid, versionCount]);

  const load = useCallback(async () => {
    if (!aggregate || entries !== null || loading) {
      return;
    }
    const versions = aggregate.versions || [];
    if (versions.length === 0) {
      setEntries({});
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const loaded = await Promise.all(
        versions.map((version) =>
          getMdsSubmissionItems(version.uuid).then((result) => ({ version, items: result.items }))
        )
      );
      if (seq !== requestSeq.current) {
        return; // a newer anchor/version superseded this request
      }
      const map: Record<string, MdsAuditEntry[]> = {};
      loaded.forEach(({ version, items }) => {
        items.forEach((item) => {
          const bucket = (map[key(item.itemCode, item.mdsColumn)] ||= []);
          bucket.push({
            versionNumber: version.versionNumber,
            submittedByRole: version.submittedByRole,
            submittedByName: version.submittedByName,
            submittedAt: version.submittedAt,
            itemValue: item.itemValue,
            sourceType: item.sourceType,
            sourceDocumentType: item.sourceDocumentType,
            sourceDateOfService: item.sourceDateOfService,
          });
        });
      });
      // newest change first
      Object.values(map).forEach((list) => list.sort((a, b) => b.versionNumber - a.versionNumber));
      setEntries(map);
    } catch (e: any) {
      if (seq === requestSeq.current) {
        setError(e.message || 'Failed to load the change history');
      }
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [aggregate, entries, loading]);

  const historyFor = useCallback(
    (itemCode: string, mdsColumn: string): MdsAuditEntry[] =>
      entries ? entries[key(itemCode, mdsColumn)] || [] : [],
    [entries]
  );

  return { load, loading, error, loaded: entries !== null, historyFor };
}
